import axiosConnector from './axiosConnector';
import axios from 'axios';
import {
  Event,
  EventResponse,
  CreateAppointmentDto,
  EquipmentType,
  Technician,
  LoginResponse,
  ServiceOrderResponse,
  UpdateServiceOrderDto,
  SettingsResponse,
  Client,
  Customer,
  ServiceOrderWithRelations,
} from './interfaces/apiInterfaces';
import { getAllCustomers } from './apiHelperCustomer';

export const fetchAppointments = async (userId: number): Promise<Event[]> => {
  try {
    // TODO: for technicians use /appointments (remove /my from the endpoints)
    
    const response = await axiosConnector.get<EventResponse>(
      `/appointments?technicianId=${userId}&limit=-1&_t=${Date.now()}`,
      {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      }
    );

    console.log('response', response);
    
    // Handle 304 or empty response data
    if (!response.data || !response.data.data) {
      console.warn('Empty or invalid response data:', response.data);
      return [];
    }
    
    return response.data.data as Event[];
  } catch (error) {
    console.error('Error fetching appointments:', error);
    throw error;
  }
};

export const fetchAppointmentsByClientId = async (
  customerId: number
): Promise<Event[]> => {
  try {
    console.log('🔔 fetchAppointmentsByClientId called with customerId:', customerId);
    const response = await axiosConnector.get<EventResponse>(
      `/appointments?customerId=${customerId}&limit=-1&_t=${Date.now()}`,
      {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      }
    );

    console.log('response', response);
    
    // Handle 304 or empty response data
    if (!response.data || !response.data.data) {
      console.warn('Empty or invalid response data:', response.data);
      return [];
    }
    
    return response.data.data as Event[];
  } catch (error) {
    console.error('Error fetching appointments:', error);
    throw error;
  }
};

export const fetchMyAppointments = async (): Promise<Event[]> => {
  try {
    const response = await axiosConnector.get<EventResponse>(
      `/appointments/my?limit=-1&_t=${Date.now()}`,
      {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      }
    );

    if (!response.data || !response.data.data) {
      return [];
    }

    return response.data.data as Event[];
  } catch (error) {
    console.error('Error fetching my appointments:', error);
    throw error;
  }
};

export const fetchAllAppointments = async (): Promise<Event[]> => {
  try {
    // Get date range for current month ± 2 weeks to ensure we get all relevant appointments
    const now = new Date();
    const twoWeeksFromNow = new Date(now);
    twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);
    
    const dateTo = twoWeeksFromNow.toISOString().split('T')[0];
    
    const response = await axiosConnector.get<EventResponse>(
      `/appointments?limit=-1&dateFrom=2025-01-01&dateTo=${dateTo}&detailed=true`
    );

    console.log('fetchAllAppointments response:', response);
    return response.data.data as Event[];
  } catch (error) {
    console.error('Error fetching appointments:', error);
    throw error;
  }
};

export const getAppointmentById = async (appointmentId: number): Promise<Event> => {
  try {
    const response = await axiosConnector.get<Event>(`/appointments/${appointmentId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching appointment by ID:', error);
    throw error;
  }
};


export const pollUnconfirmedAppointments = async (): Promise<Event[]> => {
  try {
    const response = await axiosConnector.get<EventResponse>(
      `/appointments?status=pending`
    );
    return response.data.data as Event[];
  } catch (error) {
    throw error;
  }
};

export const confirmAppointment = async (
  appointmentId: number,
  technicianId: number
) => {
  try {
    const response = await axiosConnector.post(
      `/appointments/${appointmentId}/confirm`,
      {
        technician_id: technicianId,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const refuseAppointment = async (
  appointmentId: number,
  technicianId: number
) => {
  try {
    const response = await axiosConnector.post(
      `/appointments/${appointmentId}/refuse`,
      {
        technician_id: technicianId,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const cancelAppointment = async (appointmentId: number) => {
  console.log('cancelAppointment called with appointmentId:', appointmentId);
  try {
    const response = await axiosConnector.patch(
      `/appointments/${appointmentId}`,
      {
        status: 'pending',
        technician_id: null,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateAppointment = async (appointmentId: number, data: any) => {
  try {
    const response = await axiosConnector.patch(
      `/appointments/${appointmentId}`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteAppointment = async (appointmentId: number) => {
  try {
    const response = await axiosConnector.remove(
      `/appointments/${appointmentId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Consolidated appointment creation endpoint for all roles
export const createAppointment = async (appointmentData: {
  scheduled_date: string;
  scheduled_time: string;
  status?: string;
  solicitante: string;
  tipo_problema_relatado: string;
  user_id?: number;
  address_id?: number;
  equipment_id?: number;
  technician_id?: number;
  skip_auction?: boolean;
  approval_status?: string;
  // Fields for technician self-appointments
  client_name?: string;
  client_phone?: string;
  service_address?: string;
  equipment_description?: string;
}) => {
  try {
    const response = await axiosConnector.post(
      `/appointments`,
      appointmentData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Legacy function names for backward compatibility during transition
export const createAdminAppointment = createAppointment;
export const createTechnicianSelfAppointment = createAppointment;

export const moveAppointment = async (
  appointmentId: number,
  moveData: {
    new_scheduled_date: string;
    new_scheduled_time: string;
    new_technician_id?: number | null;
    move_reason?: string;
    validate_slots?: boolean;
  }
) => {
  try {
    const response = await axiosConnector.patch(
      `/appointments/${appointmentId}/move`,
      moveData
    );

    console.log('🌐 API: moveAppointment success', {
      status: response.status,
      data: response.data,
      appointmentId
    });

    return response.data;
  } catch (error: any) {
    console.error('🌐 API: moveAppointment failed', {
      appointmentId,
      moveData,
      error: error.message,
      status: error.response?.status,
      responseData: error.response?.data,
      url: `/appointments/${appointmentId}/move`
    });
    throw error;
  }
};

export const getCompatibleTechnicians = async (appointmentId: number) => {
  try {
    console.log('🌐 API: getCompatibleTechnicians called', {
      appointmentId,
      url: `/appointments/${appointmentId}/compatible-technicians`
    });

    const response = await axiosConnector.get(
      `/appointments/${appointmentId}/compatible-technicians`
    );

    console.log('🌐 API: getCompatibleTechnicians success', {
      status: response.status,
      data: response.data,
      appointmentId
    });

    return response.data;
  } catch (error: any) {
    console.error('🌐 API: getCompatibleTechnicians failed', {
      appointmentId,
      error: error.message,
      status: error.response?.status,
      responseData: error.response?.data,
      url: `/appointments/${appointmentId}/compatible-technicians`
    });
    throw error;
  }
};

// ============= BATCH APPOINTMENT API =============

export const createBatchAppointment = async (batchAppointmentData: {
  equipment_ids: number[];
  customer_id: number;
  scheduled_date: string;
  scheduled_time: string;
  technician_id?: number;
  skip_auction?: boolean;
  solicitante?: string;
  tipo_problema_relatado?: string;
  appointment_type?: number;
  original_appointment_id?: number;
}) => {
  try {
    console.log('🌐 API: createBatchAppointment called', batchAppointmentData);
    const response = await axiosConnector.post('/appointments', batchAppointmentData);
    console.log('🌐 API: createBatchAppointment success', response.data);
    return response.data;
  } catch (error: any) {
    console.error('🌐 API: createBatchAppointment failed', {
      error: error.message,
      status: error.response?.status,
      responseData: error.response?.data,
    });
    throw error;
  }
};

export const getBatchAppointmentDetails = async (appointmentId: number): Promise<any> => {
  try {
    console.log('🌐 API: getBatchAppointmentDetails called', appointmentId);
    const response = await axiosConnector.get(`/appointments/${appointmentId}/batch-details`);
    console.log('🌐 API: getBatchAppointmentDetails success', response.data);
    return response.data;
  } catch (error: any) {
    console.error('🌐 API: getBatchAppointmentDetails failed', {
      appointmentId,
      error: error.message,
      status: error.response?.status,
      responseData: error.response?.data,
    });
    throw error;
  }
};

/**
 * @deprecated Per-equipment updates are no longer used. Batch appointments now track progress via service_order.
 * This function is kept for backward compatibility but should no longer be called.
 *
 * To update a batch appointment:
 * - Use service order update endpoints (like regular appointments)
 * - Update diagnosis, solution, parts_used via the service order
 * - Complete the batch via the service order completion flow
 */
export const updateBatchEquipmentStatus = async (
  batchEquipmentId: number,
  updateData: {
    completion_status?: 'pending' | 'completed' | 'not_applicable';
    diagnosis?: string;
    solution?: string;
    parts_used?: string;
    notes?: string;
  }
) => {
  console.warn('⚠️ updateBatchEquipmentStatus is deprecated. Use service order updates instead.');
  try {
    console.log('🌐 API: updateBatchEquipmentStatus called', {
      batchEquipmentId,
      updateData,
    });
    const response = await axiosConnector.patch(
      `/appointments/batch-equipment/${batchEquipmentId}`,
      updateData
    );
    console.log('🌐 API: updateBatchEquipmentStatus success', response.data);
    return response.data;
  } catch (error: any) {
    console.error('🌐 API: updateBatchEquipmentStatus failed', {
      batchEquipmentId,
      updateData,
      error: error.message,
      status: error.response?.status,
      responseData: error.response?.data,
    });
    throw error;
  }
};

export const cancelAppointmentByAdmin = async (appointmentId: number) => {
  console.log('cancelAppointmentByAdmin called with appointmentId:', appointmentId);
  try {
    console.log('🌐 API: cancelAppointmentByAdmin called', {
      appointmentId,
      url: `/appointments/${appointmentId}`
    });

    const response = await axiosConnector.remove(`/appointments/${appointmentId}`);
    
    console.log('🌐 API: cancelAppointmentByAdmin success', {
      appointmentId,
      response: response.data
    });
    
    return response.data;
  } catch (error: any) {
    console.error('🌐 API: cancelAppointmentByAdmin failed', {
      appointmentId,
      error: error.message,
      status: error.response?.status,
      responseData: error.response?.data,
      url: `/appointments/${appointmentId}`
    });
    throw error;
  }
};

export const deleteServiceOrder = async (serviceOrderId: number) => {
  try {
    const response = await axiosConnector.remove(
      `/service-order/${serviceOrderId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getEquipmentTypes = async (): Promise<EquipmentType[]> => {
  try {
    const response = await axiosConnector.get<EquipmentType[]>(
      `/equipment/types`
    );
    console.log('response', response);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getTechniciansByEquipmentType = async (
  equipmentTypeId: number | null
): Promise<Technician[]> => {
  try {
    const response = await axiosConnector.get<Technician[]>(
      `/users/technicians?equipmentType=${equipmentTypeId}`
    );
    console.log('response of getTechniciansByEquipmentType', response);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAvailableTechniciansByEquipmentType = async (
  equipmentTypeId: number,
  scheduledDate: string,
  scheduledTime: string,
  customerId?: number
): Promise<Technician[]> => {
  try {
    let url = `/users/technicians/available?equipmentType=${equipmentTypeId}&scheduledDate=${scheduledDate}&scheduledTime=${scheduledTime}`;
    if (customerId) {
      url += `&customerId=${customerId}`;
    }
    const response = await axiosConnector.get<Technician[]>(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAvailableTechniciansByMultipleEquipmentTypes = async (
  equipmentTypeIds: number[],
  scheduledDate: string,
  scheduledTime: string,
  customerId?: number
): Promise<Technician[]> => {
  try {
    const equipmentTypesParam = equipmentTypeIds.join(',');
    let url = `/users/technicians/available-multi?equipmentTypes=${equipmentTypesParam}&scheduledDate=${scheduledDate}&scheduledTime=${scheduledTime}`;
    if (customerId) {
      url += `&customerId=${customerId}`;
    }
    const response = await axiosConnector.get<Technician[]>(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllTechnicians = async (): Promise<Technician[]> => {
  try {
    const response = await axiosConnector.get<Technician[]>(`/users/technicians?role=technician`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getEquipmentByClient = async (clientId: number) => {
  try {
    const response = await axiosConnector.get(
      `/equipment/client/${clientId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getEquipmentById = async (equipmentId: number) => {
  try {
    const response = await axiosConnector.get(`/equipment/${equipmentId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getEquipmentByQrCode = async (qrCode: string) => {
  try {
    const response = await axiosConnector.get(`/equipment?qr_code=${qrCode}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getClientById = async (clientId: number) => {
  try {
    const response = await axiosConnector.get(`/users/${clientId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllClients = async (): Promise<Client[]> => {
  try {
    const response = await axiosConnector.get<Client[]>('/users/clients');
    console.log('getAllClients: Response data:', response.data);
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const login = async (
  emailOrUsername: string,
  password: string
): Promise<LoginResponse> => {
  try {
    console.log('Attempting login with:', { emailOrUsername });
    const response = await axiosConnector.post<LoginResponse>(
      '/auth/login',
      {
        username: emailOrUsername,
        password,
      },
      { withCredentials: true }
    );

    console.log('Login response:', response.data);

    if (!response.data.access_token) {
      throw new Error('No access token received');
    }

    return response.data;
  } catch (error: any) {
    console.error('Error during login:', error.response?.data || error.message);
    throw error;
  }
};

export const getCurrentUser = async () => {
  try {
    const response = await axiosConnector.get('/auth/me');
    return response.data;
  } catch (error) {
    console.error('Error fetching current user:', error);
    return null;
  }
};

export const getServiceOrderByAppointmentId = async (
  appointmentId: number
): Promise<ServiceOrderResponse> => {
  try {
    const response = await axiosConnector.get<ServiceOrderResponse>(
      `/service-order?appointmentId=${appointmentId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createServiceOrder = async (appointmentId: number, appointmentData: any) => {
  try {
    const payload = {
      appointment_id: appointmentId,
      order_number: `SO-${new Date().getFullYear()}-${appointmentId}`,
      diagnosis_by_client: appointmentData?.tipo_problema_relatado || 'Recreated service order',
      status_id: 1 // 'open' status
    };
    
    const response = await axiosConnector.post('/service-order', payload);
    return response.data;
  } catch (error) {
    console.error('Failed to create service order:', error);
    throw error;
  }
};

export const updateServiceOrder = async (
  serviceOrderId: number,
  data: UpdateServiceOrderDto
) => {
  try {
    const response = await axiosConnector.patch(
      `/service-order/${serviceOrderId}`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getSettings = async (userId: number): Promise<any> => {
  try {
    const response = await axiosConnector.get<SettingsResponse>(
      `/settings/${userId}`
    );

    return response.data.value;
  } catch (error) {
    throw error;
  }
};

export const updateSettings = async (userId: number, data: any) => {
  try {
    const payload = {
      user_id: userId,
      value: data,
    };
    const response = await axiosConnector.patch(`/settings/${userId}`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getGeneralSettings = async (): Promise<any> => {
  try {
    const response = await axiosConnector.get<SettingsResponse>(`/settings`);

    console.log('response general settings', response);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateGeneralSettings = async (data: any) => {
  try {
    const response = await axiosConnector.patch(`/settings`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateDangerZoneSettings = async (data: Record<string, string>) => {
  try {
    const response = await axiosConnector.patch(`/settings/danger-zone`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const register = async (
  name: string,
  email: string,
  phone: string,
  password: string,
  role: string
) => {
  try {
    const response = await axiosConnector.post('/auth/register', {
      name,
      email,
      phone,
      password,
      role,
    });
    return response.data;
  } catch (error) {
    console.error('Registration failed:', error);
    throw error;
  }
};

export const updateUserRole = async (userId: number, role: string) => {
  try {
    const response = await axiosConnector.patch(`/users/${userId}`, {
      role: role,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to update user role:', error);
    throw error;
  }
};

export const forgotPassword = async (email: string) => {
  try {
    const response = await axiosConnector.post('/auth/forgot-password', {
      email,
    });
    return response.data;
  } catch (error) {
    console.error('Forgot password failed:', error);
    throw error;
  }
};

export const resetPassword = async (token: string, password: string) => {
  try {
    const response = await axiosConnector.post('/auth/reset-password', {
      token,
      password,
    });
    return response.data;
  } catch (error) {
    console.error('Reset password failed:', error);
    throw error;
  }
};

export const updateUserProfile = async (userId: number, profileData: {
  id_photo?: string;
  social_networks?: { platform: string; profile_url: string }[];
  service_area?: string;
  equipment_type_ids?: number[];
  availability?: {
    days?: string[];
    time_slots?: string[];
    grid?: Record<string, Record<string, boolean>>;
    service_area_range?: string;
    service_area_cities?: string[];
  };
}) => {
  try {
    const response = await axiosConnector.patch(`/users/${userId}`, profileData);
    return response.data;
  } catch (error) {
    console.error('Update profile failed:', error);
    throw error;
  }
};

// ============= AVAILABILITY CONTEXT API =============

// Get all availability contexts for a technician
export const getAvailabilityContexts = async (technicianId: number) => {
  try {
    const response = await axiosConnector.get(`/users/${technicianId}/availability-contexts`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch availability contexts:', error);
    throw error;
  }
};

// Update or create a context (adminId = "independent" or number)
export const updateAvailabilityContext = async (
  technicianId: number,
  adminId: string | number,
  contextData: {
    availability_grid: Record<string, Record<string, boolean>>;
    service_area_range?: string;
    service_area_range_km?: number | null;
    service_area_cities?: string[];
  }
) => {
  try {
    const response = await axiosConnector.put(
      `/users/${technicianId}/availability-contexts/${adminId}`,
      contextData
    );
    return response.data;
  } catch (error) {
    console.error('Failed to update availability context:', error);
    throw error;
  }
};

// Delete a context
export const deleteAvailabilityContext = async (
  technicianId: number,
  adminId: string | number
) => {
  try {
    const response = await axiosConnector.remove(
      `/users/${technicianId}/availability-contexts/${adminId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to delete availability context:', error);
    throw error;
  }
};

export const checkTechnicianSlot = async (
  technicianId: number,
  date: string,
  time: string
): Promise<{ cltBlocked: boolean; independentWarning: boolean }> => {
  const response = await axiosConnector.get(
    `/users/${technicianId}/slot-check?date=${date}&time=${time}`
  );
  return response.data as { cltBlocked: boolean; independentWarning: boolean };
};

// ============= SERVICE ORDER PARTS API =============

export const createServiceOrderPart = async (serviceOrderId: number, partName: string) => {
  try {
    const response = await axiosConnector.post(`/service-order/${serviceOrderId}/parts`, {
      service_order_id: serviceOrderId,
      part_name: partName,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to create part:', error);
    throw error;
  }
};

export const getServiceOrderParts = async (serviceOrderId: number) => {
  try {
    const response = await axiosConnector.get(`/service-order/${serviceOrderId}/parts`);
    return response.data;
  } catch (error) {
    console.error('Failed to get parts:', error);
    throw error;
  }
};

export const updateServiceOrderPart = async (serviceOrderId: number, partId: number, partName: string) => {
  try {
    const response = await axiosConnector.patch(`/service-order/${serviceOrderId}/parts/${partId}`, {
      part_name: partName,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to update part:', error);
    throw error;
  }
};

export const deleteServiceOrderPart = async (serviceOrderId: number, partId: number) => {
  try {
    const response = await axiosConnector.remove(`/service-order/${serviceOrderId}/parts/${partId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete part:', error);
    throw error;
  }
};

// ============= SERVICE ORDER IMAGES API =============

export const uploadServiceOrderImage = async (
  serviceOrderId: number,
  fieldName: string,
  file: File,
  partId?: number
) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    let url = `/service-order/${serviceOrderId}/images/upload?fieldName=${fieldName}`;
    if (partId) {
      url += `&partId=${partId}`;
    }

    const response = await axiosConnector.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to upload image:', error);
    throw error;
  }
};

export const getServiceOrderImages = async (serviceOrderId: number, fieldName?: string) => {
  try {
    let url = `/service-order/${serviceOrderId}/images`;
    if (fieldName) {
      url += `?fieldName=${fieldName}`;
    }
    
    const response = await axiosConnector.get(url);
    return response.data;
  } catch (error) {
    console.error('Failed to get images:', error);
    throw error;
  }
};

export const getPartImages = async (partId: number) => {
  try {
    const response = await axiosConnector.get(`/service-order/parts/${partId}/images`);
    return response.data;
  } catch (error) {
    console.error('Failed to get part images:', error);
    throw error;
  }
};

export const deleteServiceOrderImage = async (imageId: number) => {
  try {
    const response = await axiosConnector.remove(`/service-order/images/${imageId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete image:', error);
    throw error;
  }
};

export const getImageUrl = (imageId: number): string => {
  const API_URL = process.env.REACT_APP_API_HOST || 'http://localhost:3000';
  return `${API_URL}/service-order/images/${imageId}/file`;
};

export const fetchImageAsBlob = async (imageId: number): Promise<string> => {
  try {
    const response = await axiosConnector.get(`/service-order/images/${imageId}/file`, {
      responseType: 'blob',
    });
    
    // Create blob URL for display
    const blob = response.data as Blob;
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Failed to fetch image:', error);
    throw error;
  }
};

export const getEquipmentHistory = async (
  equipmentId: number,
  customerId?: number,
  technicianId?: number,
  page: number = 1,
  limit: number = 10,
  includeOpen: boolean = false
): Promise<EventResponse> => {
  try {
    const params = new URLSearchParams();
    if (customerId) params.append('customerId', customerId.toString());
    if (technicianId) params.append('technicianId', technicianId.toString());
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (includeOpen) params.append('includeOpen', 'true');

    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await axiosConnector.get<EventResponse>(
      `/appointments/equipment/${equipmentId}/history${queryString}`,
      {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      }
    );

    if (!response.data) {
      console.warn('Empty equipment history response:', response.data);
      return { data: [], total: 0, page: 1, limit: limit };
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching equipment history:', error);
    throw error;
  }
};

// ============= SMS VERIFICATION API =============

export const generateVerificationToken = async (phone: string) => {
  try {
    const response = await axiosConnector.post('/auth/generate-2fa-token', {
      phone,
    });
    return response.data;
  } catch (error) {
    console.error('Generate verification token failed:', error);
    throw error;
  }
};

export const verifyToken = async (phone: string, token: string) => {
  try {
    const response = await axiosConnector.post('/auth/verify-2fa-token', {
      phone,
      token,
    });
    return response.data;
  } catch (error) {
    console.error('Verify token failed:', error);
    throw error;
  }
};

// ============= ACTIVITY LOG API =============

export const getActivityLogs = async (
  page: number = 1,
  limit: number = 20,
  userId?: number,
  entityType?: string
) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (userId) {
      params.append('userId', userId.toString());
    }
    
    if (entityType) {
      params.append('entityType', entityType);
    }

    const response = await axiosConnector.get(`/update-log?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch activity logs:', error);
    throw error;
  }
};

// ============= COMMANDED SPARE PARTS API =============

export const createCommandedSpareParts = async (commandedParts: any[]) => {
  try {
    const response = await axiosConnector.post('/commanded-spare-parts/bulk', commandedParts);
    return response.data;
  } catch (error) {
    console.error('Error creating commanded spare parts:', error);
    throw error;
  }
};

export const getCommandedSparePartsByServiceOrder = async (serviceOrderId: number) => {
  try {
    const response = await axiosConnector.get(`/commanded-spare-parts?serviceOrderId=${serviceOrderId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching commanded spare parts:', error);
    throw error;
  }
};

export const getCommandedSparePartsByClient = async (clientId: number) => {
  try {
    const response = await axiosConnector.get(`/commanded-spare-parts?clientId=${clientId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching commanded spare parts by client:', error);
    throw error;
  }
};

export const getCommandedSparePartsByTechnician = async (technicianId: number) => {
  try {
    const response = await axiosConnector.get(`/commanded-spare-parts?technicianId=${technicianId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching commanded spare parts by technician:', error);
    throw error;
  }
};

export const getCommandedSparePartsForAdmin = async () => {
  try {
    const response = await axiosConnector.get('/commanded-spare-parts/admin');
    return response.data;
  } catch (error) {
    console.error('Error fetching commanded spare parts for admin:', error);
    throw error;
  }
};

export const markCommandedSparePartAsAvailable = async (partId: number, adminId: number) => {
  try {
    const response = await axiosConnector.patch(`/commanded-spare-parts/${partId}/available`, { adminId });
    return response.data;
  } catch (error) {
    console.error('Error marking commanded spare part as available:', error);
    throw error;
  }
};

export const setCommandedSparePartPickupDate = async (partId: number, pickupDate: string) => {
  try {
    const response = await axiosConnector.patch(`/commanded-spare-parts/${partId}/picked-up`, { pickupDate });
    return response.data;
  } catch (error) {
    console.error('Error setting commanded spare part pickup date:', error);
    throw error;
  }
};

export const confirmBrokenPartReturn = async (
  partId: number,
  returnDate: string,
  comments: string
) => {
  try {
    const response = await axiosConnector.patch(
      `/commanded-spare-parts/${partId}/broken-returned`,
      { returnDate, comments }
    );
    return response.data;
  } catch (error) {
    console.error('Error confirming broken part return:', error);
    throw error;
  }
};

export const updateCommandedSparePartActuallyBroken = async (
  partId: number,
  isActuallyBroken: boolean
) => {
  try {
    const response = await axiosConnector.patch(
      `/commanded-spare-parts/${partId}`,
      { is_actually_broken: isActuallyBroken }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating commanded spare part actually broken status:', error);
    throw error;
  }
};

export const updateCommandedSparePartStatus = async (partId: number, status: string) => {
  try {
    const response = await axiosConnector.patch(`/commanded-spare-parts/${partId}/status`, { status });
    return response.data;
  } catch (error) {
    console.error('Error updating commanded spare part status:', error);
    throw error;
  }
};

export const deleteCommandedSparePart = async (partId: number) => {
  try {
    const response = await axiosConnector.remove(`/commanded-spare-parts/${partId}`);
    return response.data;
  } catch (error) {
    console.error('Error updating commanded spare part:', error);
    throw error;
  }
};

export const confirmCommandedSpareParts = async (serviceOrderId: number) => {
  try {
    const response = await axiosConnector.patch(`/commanded-spare-parts/service-order/${serviceOrderId}/confirm`);
    return response.data;
  } catch (error) {
    console.error('Error confirming commanded spare parts:', error);
    throw error;
  }
};

export const getMissedNotifications = async (lastLoginTime: string) => {
  try {
    const response = await axiosConnector.get(`/notifications`, {
      params: {
        status: 'missed',
        lastLoginTime,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching missed notifications:', error);
    throw error;
  }
};

export const markNotificationAsRead = async (notificationId: string | number) => {
  try {
    const response = await axiosConnector.patch(`/notifications/${notificationId}/read`);
    return response.data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

export const markAllNotificationsAsRead = async () => {
  try {
    const response = await axiosConnector.patch(`/notifications/mark-all-read`);
    return response.data;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

export const deleteNotificationFromDb = async (notificationId: string | number) => {
  try {
    const response = await axiosConnector.remove(`/notifications/${notificationId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

export const deleteAllNotificationsFromDb = async () => {
  try {
    const response = await axiosConnector.remove(`/notifications`);
    return response.data;
  } catch (error) {
    console.error('Error deleting all notifications:', error);
    throw error;
  }
};

export const getActivityLogUsers = async () => {
  try {
    const response = await axiosConnector.get('/update-log/users');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch activity log users:', error);
    throw error;
  }
};

export const getActivityLogActions = async () => {
  try {
    const response = await axiosConnector.get('/update-log/actions');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch activity log actions:', error);
    throw error;
  }
};

// ============= RETURN APPOINTMENT API =============

export const getReturnAppointment = async (originalAppointmentId: number) => {
  try {
    const response = await axiosConnector.get(`/appointments/${originalAppointmentId}/return-appointment`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch return appointment:', error);
    throw error;
  }
};

export const deleteReturnAppointment = async (originalAppointmentId: number) => {
  try {
    const response = await axiosConnector.remove(`/appointments/${originalAppointmentId}/return-appointment`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete return appointment:', error);
    throw error;
  }
};

export const getAppointmentsForExport = async (
  fromDate: string,
  untilDate: string,
  includeOpen?: boolean
): Promise<any[]> => {
  try {
    const response = await axiosConnector.get<any[]>('/appointments/export/data', {
      params: {
        fromDate,
        untilDate,
        includeOpen: includeOpen ? 'true' : 'false',
      },
    });
    return response.data || [];
  } catch (error) {
    console.error('Failed to fetch appointments for export:', error);
    throw error;
  }
};

// ============= MIFIX INTEGRATION API =============

export const fetchMiFixEquipmentData = async (ativoId: string): Promise<any> => {
  try {
    const response = await axios.get(`https://slweb.slaplic.com.br:3001/mifix/consulta/ativo/${ativoId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching MiFix equipment data:', error);
    if (error.response?.status === 404) {
      throw new Error('Equipamento não encontrado no sistema MiFix');
    }
    if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
      throw new Error('Não foi possível conectar ao sistema MiFix. Verifique sua conexão.');
    }
    throw new Error('Erro ao buscar dados do equipamento no MiFix');
  }
};

export const searchAddressByFields = async (addressData: {
  streetAndNumber?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}): Promise<number | null> => {
  try {
    return null;
  } catch (error: any) {
    console.error('Error searching address:', error);
    return null;
  }
};

export const createAddress = async (addressData: {
  streetAndNumber: string;
  complement?: string;
  neighborhood?: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
}): Promise<{ id: number }> => {
  try {
    const response = await axiosConnector.post<{ id: number }>('/users/address', {
      streetAndNumber: addressData.streetAndNumber,
      city: addressData.city,
      state: addressData.state,
      zipCode: addressData.zipCode,
      country: addressData.country || 'Brasil',
      neighborhood: addressData.neighborhood,
      complemento: addressData.complement
    });
    return response.data;
  } catch (error) {
    console.error('Error creating address:', error);
    throw error;
  }
};

export const searchCustomerByName = async (customerName: string): Promise<any | null> => {
  try {
    const normalizedName = customerName.trim();
    const response = await getAllCustomers();
    const allCustomers = response.customers;

    const exactMatch = allCustomers.find((c: Customer) =>
      c.name?.trim() === normalizedName
    );

    if (exactMatch) {
      return exactMatch;
    }

    const caseInsensitiveMatch = allCustomers.find((c: Customer) =>
      c.name?.trim().toLowerCase() === normalizedName.toLowerCase()
    );
    
    if (caseInsensitiveMatch) {
      return caseInsensitiveMatch;
    }
    
    return null;
  } catch (error: any) {
    console.error('Error searching customer:', error);
    throw error;
  }
};

export const getOrCreateEquipmentType = async (typeName: string): Promise<{ id: number }> => {
  try {
    const existingTypes = await getEquipmentTypes();
    
    const normalizedTypeName = typeName.trim().toLowerCase();
    
    const existingType = existingTypes.find(
      (type: EquipmentType) => {
        const normalizedDescription = (type.description || '').trim().toLowerCase();
        return normalizedDescription === normalizedTypeName || 
               normalizedDescription.includes(normalizedTypeName) ||
               normalizedTypeName.includes(normalizedDescription);
      }
    );
    
    if (existingType) {
      return { id: existingType.id };
    }

    if (existingTypes.length > 0) {
      console.warn(`Equipment type "${typeName}" not found, using first available type`);
      return { id: existingTypes[0].id };
    }
    
    throw new Error('Nenhum tipo de equipamento disponível no sistema');
  } catch (error: any) {
    console.error('Error getting equipment type:', error);
    throw error;
  }
};

export const createEquipmentFromMiFix = async (equipmentData: {
  qr_id: string;
  serial_number: string;
  model: string;
  equipment_type_id: number;
  building_id: number;
}): Promise<any> => {
  try {
    const createPayload: any = {
      buildingId: equipmentData.building_id,
      typeId: equipmentData.equipment_type_id,
      name: equipmentData.qr_id,
      brand: 'N/A',
      model: equipmentData.model || '',
      serialNumber: equipmentData.serial_number || '',
      location: '',
      qrId: equipmentData.qr_id
    };
    
    const createResponse = await axiosConnector.post<any>('/equipment', createPayload);
    const createdEquipment = createResponse.data as any;
    
    if (!createdEquipment.qr_id && equipmentData.qr_id) {
      console.warn('qr_id was not set during creation. Backend DTO needs to be updated to include qr_id field.');
    }

    return createdEquipment;
  } catch (error) {
    console.error('Error creating equipment from MiFix:', error);
    throw error;
  }
};

export const fetchAllServiceOrders = async (): Promise<ServiceOrderWithRelations[]> => {
  try {
    const response = await axiosConnector.get('/service-order/all');
    return response.data as ServiceOrderWithRelations[];
  } catch (error) {
    console.error('Error fetching all service orders:', error);
    throw error;
  }
};
